import type { JSX } from "react";
import { useEffect, useState } from "react";
import { useCarrierContext } from "../lib/useCarrierContext";
import { AgentGraphPanel } from "./AgentGraphPanel";
import { TritonChat } from "./TritonChat";
import type { ConnectionStatus, EnvelopeFromClient, EnvelopeFromServer } from "../lib/aide-protocol";

// UC-12 parent surface: hosts the existing chat (left) + new
// AgentGraphPanel (right). The screen owns the current turn_id /
// deal_id state and pushes it to the graph panel.
//
// Renders carrier-aware settlement chips between the two panes so
// the consumer sees "PayPay" or "au PAY / JPYC / BTC / ETH / USDC"
// directly above the conversation.

export interface TritonChatScreenProps {
  consumerId: string;
  status: ConnectionStatus;
  send: (envelope: EnvelopeFromClient) => void;
  envelopes: EnvelopeFromServer[];
  pendingAttachmentCount: number;
}


export function TritonChatScreen(props: TritonChatScreenProps): JSX.Element {
  const carrier = useCarrierContext(props.consumerId);
  const [turnId, setTurnId] = useState<string | null>(null);
  const [dealId, setDealId] = useState<string | null>(null);

  // Detect turn_id / deal_id from incoming envelopes so the graph
  // panel knows which turn to subscribe to. Triton emits
  // turn_started early in every turn; PACT auction broadcasts
  // carry a deal_id we pass through to the unified stream.
  useEffect(() => {
    for (const env of props.envelopes) {
      const turn = (env as { turn_id?: string }).turn_id;
      if (turn && turn !== turnId) {
        setTurnId(turn);
      }
      const deal = (env as { deal_id?: string }).deal_id;
      if (deal && deal !== dealId) {
        setDealId(deal);
      }
    }
  }, [props.envelopes, turnId, dealId]);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 480px",
        gap: 16,
        minHeight: "100vh",
        background: "#020617",
      }}
      data-carrier={carrier.carrierId}
    >
      <main style={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
        <CarrierChipsHeader carrier={carrier} />
        <div style={{ flex: 1, minHeight: 0 }}>
          <TritonChat
            status={props.status}
            send={props.send}
            envelopes={props.envelopes}
            pendingAttachmentCount={props.pendingAttachmentCount}
          />
        </div>
      </main>
      <AgentGraphPanel
        turnId={turnId}
        dealId={dealId}
        carrier={carrier}
      />
    </div>
  );
}


function CarrierChipsHeader({ carrier }: { carrier: ReturnType<typeof useCarrierContext> }): JSX.Element {
  return (
    <header
      style={{
        padding: "10px 16px",
        background: "#0f172a",
        borderBottom: "1px solid #1e293b",
        display: "flex", alignItems: "center", gap: 10,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        color: "#e2e8f0",
      }}
      aria-label="Settlement options for this consumer"
    >
      <span style={{ fontSize: 13, fontWeight: 600 }}>
        {carrier.settlementBrand}
      </span>
      <span style={{ fontSize: 11, color: "#64748b" }}>·</span>
      {carrier.supportedDenominations.map((denom) => (
        <span
          key={denom}
          style={{
            fontSize: 11, padding: "3px 8px",
            background: "#1e293b", borderRadius: 999,
            color: "#cbd5e1",
            fontFamily: '"SF Mono", Menlo, monospace',
          }}
        >
          {denom.toUpperCase()}
        </span>
      ))}
      <span style={{ flex: 1 }} />
      {carrier.loyaltyBalance !== null && carrier.loyaltyBalance > 0 && (
        <span
          style={{
            fontSize: 11, padding: "3px 8px",
            background: "#fbbf24", color: "#1f2937",
            borderRadius: 999, fontWeight: 600,
          }}
          aria-label={`${carrier.loyaltyProgramName} balance`}
        >
          {carrier.loyaltyProgramName}: {carrier.loyaltyBalance.toLocaleString()}{" "}
          {carrier.loyaltyUnitsLabel}
        </span>
      )}
    </header>
  );
}
